import { type ReactNode, createContext, useContext } from 'react';

import { CaregiverRoleEnum } from '@/shared/enums';

import { ICaregiverConsultationDto } from '@/caregiverSpace/shared/model';

type RenderPastConsultationFn = (
    consultationId: string,
    onClose: () => void,
    caregiverRole: CaregiverRoleEnum,
    consultationCaregiverRoles: CaregiverRoleEnum[],
) => React.ReactNode;

const PastConsultationContext = createContext<RenderPastConsultationFn | null>(
    null,
);

export const PastConsultationProvider = PastConsultationContext.Provider;

export const useRenderPastConsultation = (): RenderPastConsultationFn => {
    const renderPastConsultation = useContext(PastConsultationContext);
    if (!renderPastConsultation) {
        throw new Error(
            'useRenderPastConsultation must be used within a PastConsultationProvider',
        );
    }
    return renderPastConsultation;
};

type RenderTextProfileConsultationFn = (
    consultationUid: string,
    onClose: () => void,
    shouldReserveConsultationOnWriteButton: boolean,
    assignedCaregiverUserId?: number | null,
    shouldHideEndTextConsultationButton?: boolean,
    caregiverRole?: CaregiverRoleEnum,
    consultationCaregiverRoles?: CaregiverRoleEnum[],
) => React.ReactNode;

const TextProfileConsultationContext =
    createContext<RenderTextProfileConsultationFn | null>(null);

export const TextProfileConsultationProvider =
    TextProfileConsultationContext.Provider;

export const useRenderTextProfileConsultation =
    (): RenderTextProfileConsultationFn => {
        const renderTextProfileConsultation = useContext(
            TextProfileConsultationContext,
        );
        if (!renderTextProfileConsultation) {
            throw new Error(
                'useRenderTextProfileConsultation must be used within a TextProfileConsultationProvider',
            );
        }
        return renderTextProfileConsultation;
    };

type RenderTodayConsultationFn = (
    consultation: ICaregiverConsultationDto,
    onClose: () => void,
) => React.ReactNode;

const TodayConsultationContext =
    createContext<RenderTodayConsultationFn | null>(null);

export const TodayConsultationProvider = TodayConsultationContext.Provider;

export const useRenderTodayConsultation = (): RenderTodayConsultationFn => {
    const renderTodayConsultation = useContext(TodayConsultationContext);
    if (!renderTodayConsultation) {
        throw new Error(
            'useRenderTodayConsultation must be used within a TodayConsultationProvider',
        );
    }
    return renderTodayConsultation;
};

type RenderUpcomingConsultationFn = (
    consultationUid: string,
    onClose: () => void,
) => React.ReactNode;

const UpcomingConsultationContext =
    createContext<RenderUpcomingConsultationFn | null>(null);

export const UpcomingConsultationProvider =
    UpcomingConsultationContext.Provider;

export const useRenderUpcomingConsultation =
    (): RenderUpcomingConsultationFn => {
        const renderUpcomingConsultation = useContext(
            UpcomingConsultationContext,
        );
        if (!renderUpcomingConsultation) {
            throw new Error(
                'useRenderUpcomingConsultation must be used within a UpcomingConsultationProvider',
            );
        }
        return renderUpcomingConsultation;
    };

interface ConsultationProviderProps {
    past: RenderPastConsultationFn;
    textProfile: RenderTextProfileConsultationFn;
    today: RenderTodayConsultationFn;
    upcoming: RenderUpcomingConsultationFn;
    children: ReactNode;
}

export const ConsultationProvider = ({
    past,
    textProfile,
    today,
    upcoming,
    children,
}: ConsultationProviderProps) => (
    <PastConsultationProvider value={past}>
        <TextProfileConsultationProvider value={textProfile}>
            <TodayConsultationProvider value={today}>
                <UpcomingConsultationProvider value={upcoming}>
                    {children}
                </UpcomingConsultationProvider>
            </TodayConsultationProvider>
        </TextProfileConsultationProvider>
    </PastConsultationProvider>
);
